<script setup>
import { debounce, subStringToType } from '@/utils/index'
import {
  prescription,
  health,
  offLinePrescriptionList,
  prescriptionGroupList
} from '@/api/workPlace/prescription'
import { watch, ref, reactive, computed, defineProps } from 'vue'
import { useStore } from 'vuex'
import {
  columnsToOnline,
  columnsToOffline,
  columnsToHealth,
  prescriptionOption,
  columnsGroup,
  healthOption,
  onLineOption
} from './config'
import TheDetail from '@/components/TheDetail/index.vue'
import { calcDiagnosis } from '@/utils/index'
const props = defineProps({
  ucUserId: {
    type: Number,
    default: null
  },
  mode: {
    type: String,
    default: ''
  }
})
const store = useStore()
// 患者 id
const ucPatientId = computed(() => {
  return props?.ucUserId || store?.state?.workOrder?.workOrderGroupInfo?.patientInfo?.ucUserId || ''
})
// NOTE:关于列表
// 详情页的显示隐藏
const loading = ref(false)
const visible = ref(false)
// 表格内的数据
const data = reactive({
  // 分页器
  pagination: {
    current: 1,
    pageSize: 10,
    showQuickJumper: true,
    showSizeChanger: true,
    total: 100,
    showTotal: () => {}
  },
  // 表格
  table: [],
  // 表格中被点击的每一条数据的唯一标识：serialNumber (线下弃用)
  serialNumber: '',
  // 随访id
  id: '0',
  // 显示TheDetail中的详情页
  recordType: 'visit'
})
// 点击后的操作
const onOperation = obj => {
  visible.value = true
  data.serialNumber = obj.record.serialNumber
  if (recordType.value === 'prescriptionGroup') {
    data.recordType = 'prescriptionGroup'
    data.serialNumber = obj.record.parentSerialNumber
    return
  }
  data.recordType = subStringToType(data.serialNumber)
}
// 联合处方详情
const getGroupDetails = parentSn => {
  visible.value = true
  data.serialNumber = parentSn
  data.recordType = 'prescriptionGroup'
}
// 打开随访详情详情 新的不打开
function openVisit(record, id) {
  if (!id) return
  if (!record.ifNew) {
    visible.value = true
    data.id = id
    data.recordType = 'visit'
  }
}
// 改变当前页 和 改变页码值
function onPageOrSize({ current, pageSize = 10 }) {
  data.pagination.pageSize = pageSize
  data.pagination.current = current
  getTable()
}
// 获取电子处方列表信息
function getPrescription() {
  prescription(
    ucPatientId.value,
    data.pagination.current,
    data.pagination.pageSize,
    queryType.value
  )
    .then(resolve => {
      const [tmp] = resolve
      data.table = tmp.result
      data.pagination.current = tmp.pageNo
      data.pagination.pageSize = tmp.pageSize
      data.pagination.total = tmp.totalCount
      data.pagination.showTotal = () => `共${tmp.totalCount}条`
    })
    .finally(() => (loading.value = false))
}
// 健康处方列表信息
function getHealth() {
  health(ucPatientId.value, data.pagination.current, data.pagination.pageSize, queryType.value)
    .then(resolve => {
      const [tmp] = resolve
      data.table = tmp.result
      data.pagination.current = tmp.pageNo
      data.pagination.pageSize = tmp.pageSize
      data.pagination.total = tmp.totalCount
      data.pagination.showTotal = () => `共${tmp.totalCount}条`
    })
    .finally(() => (loading.value = false))
}
// 线下处方列表接口变更
function getPrescriptionOffline() {
  offLinePrescriptionList({
    ucPatientId: ~~ucPatientId.value,
    page: data.pagination.current,
    num: data.pagination.pageSize
  })
    .then(resolve => {
      const res = resolve || {}
      data.table = res.result
      data.pagination.current = res.pageNo
      data.pagination.pageSize = res.pageSize
      data.pagination.total = res.totalCount
      data.pagination.showTotal = () => `共${res.totalCount}条`
    })
    .finally(() => (loading.value = false))
}
// 联合处方查询
const getPrescriptionGroup = async () => {
  try {
    const [res] = await prescriptionGroupList(
      ucPatientId.value,
      data.pagination.current,
      data.pagination.pageSize,
      queryType.value
    )
    data.table = res.result
    data.pagination.current = res.pageNo
    data.pagination.pageSize = res.pageSize
    data.pagination.total = res.totalCount
    data.pagination.showTotal = () => `共${res.totalCount}条`
    loading.value = false
  } catch (error) {
    loading.value = false
  }
}
// 获取数据
const getTableOfDebounce = debounce(() => {
  switch (recordType.value) {
    case 'prescriptionOnline':
      getPrescription()
      break
    case 'prescriptionOffline':
      getPrescriptionOffline()
      break
    case 'prescriptionHealth':
      getHealth()
      break
    case 'prescriptionGroup':
      getPrescriptionGroup()
      break
  }
})
function getTable() {
  data.table.splice(0)
  loading.value = true
  getTableOfDebounce()
}
// NOTE:下拉选 or 切换 tab 触发事件
// 下拉选中的查询条件
const queryType = ref(-1)
// 表格列
const columns = ref(columnsToOnline)
// 当前是线上处方、线下处方 or 辅助
const recordType = ref('prescriptionGroup')
// 下拉选
const options = ref(onLineOption)
watch(
  recordType,
  type => {
    queryType.value = -1
    switch (type) {
      // 线上
      case 'prescriptionOnline':
        options.value = onLineOption
        columns.value = columnsToOnline
        break
      // 线下
      case 'prescriptionOffline':
        options.value = prescriptionOption
        columns.value = columnsToOffline
        break
      //联合处方
      case 'prescriptionGroup':
        options.value = prescriptionOption
        columns.value = columnsGroup
        break
      // 健康
      default:
        options.value = healthOption
        columns.value = columnsToHealth
        break
    }
    onPageOrSize({ current: 1, pageSize: 10 })
  },
  { immediate: true }
)
watch(
  () => visible.value,
  () => {
    getTable()
  }
)
</script>

<template>
  <div class="prescription">
    <!-- 头部切换 -->
    <header class="header">
      <a-radio-group v-model:value="recordType" button-style="solid" :disabled="loading">
        <a-radio-button class="radio" value="prescriptionGroup">治疗方案</a-radio-button>
        <a-radio-button class="radio" value="prescriptionOnline">线上处方</a-radio-button>
        <a-radio-button class="radio" value="prescriptionHealth">健康辅助方</a-radio-button>
        <a-radio-button value="prescriptionOffline">线下处方</a-radio-button>
      </a-radio-group>
    </header>
    <!-- 查询 线下处方没有这个选项-->
    <div class="search">
      <a-select
        v-show="recordType !== 'prescriptionOffline' && recordType !== 'prescriptionGroup'"
        class="search-select"
        v-model:value="queryType"
      >
        <a-select-option :value="option.value" :key="option.value" v-for="option in options">
          {{ option.label }}
        </a-select-option>
      </a-select>
      <a-button
        v-show="recordType !== 'prescriptionOffline' && recordType !== 'prescriptionGroup'"
        @click="onPageOrSize({ current: 1, pageSize: 10 })"
        class="search-button"
        type="primary"
      >
        搜索
      </a-button>
    </div>
    <!-- 表格 -->
    <a-table
      :loading="loading"
      class="table"
      :pagination="data.pagination"
      :rowKey="(record, index) => index"
      :columns="columns"
      :data-source="data.table"
      @showSizeChange="onPageOrSize"
      @change="onPageOrSize"
      :transformCellText="({ text }) => text ?? '--'"
      show-quick-jumper
      :scroll="{
        x: '100%',
        y: recordType === 'prescriptionOffline' ? 'calc(100vh - 290px)' : 'calc(100vh - 330px)'
      }"
    >
      <!-- 诊断 -->
      <template #diagnosisInfoList="{ record }">
        <span class="elip--multiple" style="-webkit-line-clamp: 2">
          {{
            calcDiagnosis(
              record,
              'diagnosisInfoList',
              recordType === 'prescriptionOnline' ? 'diagnosis' : 'diagnosisList'
            )
          }}
        </span>
      </template>
      <template #MedicineList="{ record }">
        <div class="elip--multiple" style="-webkit-line-clamp: 2">
          {{
            record.offlineMedicineList &&
            record.offlineMedicineList.map(item => item.name).join(',')
          }}
        </div>
      </template>
      <!-- 随访详情 -->
      <template #followupPlanIds="{ record, text }">
        <template v-if="text && text.length">
          <a-button
            v-for="(it, index) in text"
            :key="it"
            :class="[!record.ifNew && 'button-link']"
            :type="!record.ifNew ? 'link' : 'text'"
            @click="openVisit(record, it)"
          >
            {{ it }}
            <span v-if="text.length !== index + 1">,</span>
          </a-button>
        </template>
        <span v-else>--</span>
      </template>
      <!-- 操作 -->
      <template #handle="data">
        <a-button class="button-link" type="link" @click="onOperation(data)">查看详情</a-button>
        <a-button
          v-if="data.record.parentSn"
          class="button-link"
          type="link"
          @click="getGroupDetails(data.record.parentSn)"
        >
          查看治疗方案
        </a-button>
      </template>
    </a-table>
    <!-- 弹出框 -->
    <the-detail
      v-model:visible="visible"
      :id="data.id"
      :serialNumber="data.serialNumber"
      :recordType="data.recordType"
      :mode="mode"
    />
  </div>
</template>

<style lang="scss" scoped>
.prescription {
  .header {
    padding: 8px 20px;
    background: #f3f6fa;
    position: sticky;
    top: 0;
    z-index: 1;
    ::v-deep(.ant-radio-button-wrapper) {
      border-radius: 4px;
      border: 1px solid #999999;
      color: #999999;
      background: none;
    }

    ::v-deep(
        .ant-radio-group-solid
          .ant-radio-button-wrapper-checked:not(.ant-radio-button-wrapper-disabled)
      ) {
      background: none;
      border: 1px solid #1890ff;
      color: #1890ff;
    }
    ::v-deep(.ant-radio-button-wrapper:not(:first-child)::before) {
      display: none;
    }
    .radio {
      margin-right: 40px;
    }
  }
  .search {
    text-align: right;
    margin: 10px 20px 0;
    &-select {
      margin-right: 12px;
      width: 112px;
      text-align: left;
    }
  }
  .table {
    padding: 10px 20px 0;
    .button-link {
      padding: 0;
    }
  }
}
</style>
